import { UseQueryOptions, UseSuspenseQueryOptions, keepPreviousData, useMutation } from '@tanstack/react-query';

import { getQueryKey, rethrowDecodedBlobError, saveBlobResponse } from '@feature/utils';

import * as api from './TravelRegistryApi';
import {
  TravelAggregates,
  TravelRegistryExportRequest,
  TravelRegistryPage,
  TravelRegistryParams,
  TravelReportingParams,
  TravelStaffSummaryExportRequest,
  TravelYearlyAggregatesParams,
  WorldTopology,
} from './TravelRegistryTypes';

export const travelRegistryKey = getQueryKey('travelRegistry');

export function getTravelRegistryPagedOptions(
  params: TravelRegistryParams,
): UseSuspenseQueryOptions<TravelRegistryPage> {
  return {
    queryKey: travelRegistryKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

const REGISTRY_EXPORT_NAMES = {
  ERASMUS: 'Registrul_mobilitatilor_Erasmus',
  RESEARCH: 'Registrul_deplasarilor_cadrelor_didactice_si_cercetatorilor_la_manifestari_stiintifice_-_educationale',
} as const;

function registryExportName(registryType: TravelRegistryExportRequest['registryType']) {
  return registryType ? REGISTRY_EXPORT_NAMES[registryType] : 'Registrul_deplasarilor';
}

export function useTravelRegistryExport() {
  return useMutation({
    mutationFn: (params: TravelRegistryExportRequest) =>
      api
        .exportRegistry(params)
        .then(response =>
          saveBlobResponse(
            response,
            // Mirrors the server's own name so a missing Content-Disposition cannot save the wide sheet
            // under the compact one's name, nor one registry under the other's.
            `${registryExportName(params.registryType)}${params.registrationYear != null ? `_${params.registrationYear}` : ''}${params.full ? '_COMPLET' : ''}.xlsx`,
          ),
        )
        .catch(rethrowDecodedBlobError),
  });
}

// Non-suspense on purpose: a failed panel must not blank the filters and export above it.
export function getTravelAggregatesOptions(params: TravelReportingParams): UseQueryOptions<TravelAggregates> {
  return {
    queryKey: travelRegistryKey.as(['aggregates', params]),
    queryFn: () => api.getAggregates(params),
    placeholderData: keepPreviousData,
  };
}

export function getTravelYearlyAggregatesOptions(
  params: TravelYearlyAggregatesParams,
): UseQueryOptions<TravelAggregates[]> {
  return {
    queryKey: travelRegistryKey.as(['aggregates', 'yearly', params]),
    queryFn: () => api.getYearlyAggregates(params),
    placeholderData: keepPreviousData,
  };
}

// The world map never changes.
export function getWorldTopologyOptions(): UseQueryOptions<WorldTopology> {
  return {
    queryKey: travelRegistryKey.as(['world-topology']),
    queryFn: api.getWorldTopology,
    staleTime: Infinity,
    gcTime: Infinity,
  };
}

export function useTravelStaffSummaryExport() {
  return useMutation({
    mutationFn: (params: TravelStaffSummaryExportRequest) =>
      api
        .exportStaffSummary(params)
        .then(response =>
          saveBlobResponse(
            response,
            `Evidenta_deplasarilor_${params.registrationYear ?? new Date().getFullYear()}.xlsx`,
          ),
        )
        .catch(rethrowDecodedBlobError),
  });
}
